'use client';

/**
 * `ExperienceStep` — the canonical dancer experience editor (training,
 * employment, performances, competitions, awards). Used by the profile setup
 * wizard, the /edit page, the apply wizard, and the admin candidate sheet.
 *
 * This file is now a thin render shell: all state + handlers live in the
 * `useExperienceManager` hook; the inline add/edit form, the read-only cards,
 * and the three mode layouts live in sibling modules. The component composes
 * them. The split is extraction-only — there is no parallel field list, Zod
 * schema, or mapper (single edit surface, Critical Rule #8).
 */
import { ExperienceAddForm } from './experience-add-form';
import {
  AWARD_CATEGORY_TYPES,
  EXPERIENCE_CATEGORY_TYPES,
  type ExperienceType,
} from './experience-step-helpers';
import {
  ExperienceProfileView,
  SelectAffiliationView,
  SelectExperienceView,
} from './experience-step-views';
import type {
  ExperienceStepMode,
  ExperienceStepProps,
} from './experience-step.types';
import { useExperienceManager } from './use-experience-manager';

// Re-export the public types so existing consumers of
// `@kit/profiles/components` keep importing `ExperienceStepMode` /
// `ExperienceType` from here unchanged.
export type { ExperienceStepMode, ExperienceType };

export function ExperienceStep(props: ExperienceStepProps) {
  const {
    professionalProfileId,
    mode = 'profile',
    title,
    freelancerSection,
    actorRole = 'self',
    onBehalfOfDancerId,
    auditionId,
    readOnly = false,
  } = props;

  // Admin mode prerequisites — fail loudly in dev rather than silently
  // sending incomplete OnBehalf payloads. Runs before any hook (identical to
  // the previous inline guard).
  if (actorRole === 'admin' && (!onBehalfOfDancerId || !auditionId)) {
    throw new Error(
      'ExperienceStep: actorRole="admin" requires both onBehalfOfDancerId and auditionId props',
    );
  }

  // All state + handlers + effects live in this hook. Calling it
  // unconditionally at the top preserves the exact hook-call order, so the
  // BALLEE-52 invariant ("never change the number/order of hooks between
  // renders") holds — the early return below still runs AFTER every hook.
  const mgr = useExperienceManager(props);

  // Now safe to return early — the guard runs after all hooks.
  if (!professionalProfileId) {
    return (
      <div className="space-y-6 pb-28 sm:pb-8">
        <p className="text-muted-foreground text-sm">
          Complete previous steps first to add your professional experience.
        </p>
      </div>
    );
  }

  // Inline add/edit form — shared across all modes.
  const addFormNode = (
    <ExperienceAddForm
      t={mgr.t}
      isAwardMode={mgr.isAwardMode}
      editingId={mgr.editingId}
      formType={mgr.formType}
      isPending={mgr.isPending}
      selectedOrg={mgr.selectedOrg}
      hasOrgSelection={mgr.hasOrgSelection}
      onOrgSelect={mgr.onOrgSelect}
      onOrgDeselect={mgr.onOrgDeselect}
      onOrgCreate={mgr.onOrgCreate}
      achievementPromptName={mgr.achievementPromptName}
      onAchievementConfirm={mgr.confirmAchievement}
      onAchievementOverride={mgr.overrideAsOrganization}
      onFormTypeChange={mgr.setFormType}
      placement={mgr.placement}
      setPlacement={mgr.setPlacement}
      awardYear={mgr.awardYear}
      setAwardYear={mgr.setAwardYear}
      awardTitle={mgr.awardTitle}
      setAwardTitle={mgr.setAwardTitle}
      availableRanks={mgr.availableRanks}
      selectedRank={mgr.selectedRank}
      setSelectedRank={mgr.setSelectedRank}
      startDate={mgr.startDate}
      setStartDate={mgr.setStartDate}
      endDate={mgr.endDate}
      setEndDate={mgr.setEndDate}
      isCurrent={mgr.isCurrent}
      setIsCurrent={mgr.setIsCurrent}
      isWinner={mgr.isWinner}
      setIsWinner={mgr.setIsWinner}
      onSubmit={mgr.handleSubmitExperience}
      onCancel={() => mgr.resetForm({ dismissedByUser: true })}
    />
  );

  // ---------------------------------------------------------------------------
  // SELECT-AFFILIATION mode — RadioGroup of current school/company
  // ---------------------------------------------------------------------------
  if (mode === 'select-affiliation') {
    return (
      <SelectAffiliationView
        candidates={mgr.affiliationList}
        selectedSingleId={mgr.selectedSingleId}
        onSelectSingle={mgr.setSingle}
        showAddForm={mgr.showAddForm}
        addForm={addFormNode}
        onAddNew={() => {
          mgr.openExperienceForm();
          // Pre-set "currently here" because affiliation = current.
          mgr.setIsCurrent(true);
        }}
        isLoading={mgr.isLoading}
      />
    );
  }

  // ---------------------------------------------------------------------------
  // SELECT-EXPERIENCE mode — checkbox list of past experiences
  // ---------------------------------------------------------------------------
  if (mode === 'select-experience') {
    return (
      <SelectExperienceView
        candidates={mgr.pastList}
        selectedMultiIds={mgr.selectedMultiIds}
        onTogglePast={mgr.togglePast}
        showAddForm={mgr.showAddForm}
        addForm={addFormNode}
        onAddNew={mgr.openExperienceForm}
        isLoading={mgr.isLoading}
      />
    );
  }

  // ---------------------------------------------------------------------------
  // PROFILE mode (default) — full management with category groups
  // ---------------------------------------------------------------------------

  // Determine which sections to render based on filterTypes
  const { filterTypes } = props;
  const onlyAwards = !!(
    filterTypes &&
    filterTypes.length > 0 &&
    filterTypes.every((ft) => AWARD_CATEGORY_TYPES.includes(ft))
  );
  const onlyExperiences = !!(
    filterTypes &&
    filterTypes.length > 0 &&
    filterTypes.every((ft) => EXPERIENCE_CATEGORY_TYPES.includes(ft))
  );

  const showExperienceSection = !onlyAwards;
  const showAwardSection = !onlyExperiences;

  // Once the user has at least one experience, the entry-point button reads
  // "Add another …" and goes primary so it's the obvious next step after a
  // save. Empty state shows outline buttons (the form is auto-opened above
  // anyway, so these are just the explicit re-open path after a Cancel).
  const hasAnyExperience = mgr.experienceList.length > 0;

  return (
    <ExperienceProfileView
      experienceList={mgr.experienceList}
      awardList={mgr.awardList}
      showExperienceSection={showExperienceSection}
      showAwardSection={showAwardSection}
      onlyAwards={onlyAwards}
      hasAnyExperience={hasAnyExperience}
      title={title}
      showAddForm={mgr.showAddForm}
      addForm={addFormNode}
      isPending={mgr.isPending}
      editingId={mgr.editingId}
      actorRole={actorRole}
      readOnly={readOnly}
      t={mgr.t}
      onEdit={mgr.handleEditExperience}
      onDelete={mgr.handleDelete}
      onOpenExperienceForm={mgr.openExperienceForm}
      onOpenAwardForm={mgr.openAwardForm}
      freelancerSection={freelancerSection}
      isLoading={mgr.isLoading}
    />
  );
}
